import CompanyBrandingService from '@apis/CompanyBrandingService';

// Module-level cache for pre-fetched brand assets.
// The onboarding drawer starts polling as soon as the company URL is entered,
// so brand data (logo, screenshot) is ready by the time WelcomeModal opens.

interface BrandCache {
  logos: string[];
  screenshotUrl: string | null;
  description: string;
  conventions: string;
}

let cache: BrandCache | null = null;
let polling = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;

const POLL_MS = 3000;
const MAX_POLLS = 20; // stop after ~60s

export function startBrandPrefetch(companyName: string) {
  if (polling || !companyName) return;
  polling = true;
  let attempts = 0;

  const poll = async () => {
    attempts++;
    try {
      const assets = await CompanyBrandingService.getBrandAssets(companyName);
      cache = {
        logos: assets.logos ?? [],
        screenshotUrl: assets.screenshot_url || null,
        description: assets.description ?? '',
        conventions: assets.conventions ?? '',
      };
      // Keep polling until we have a screenshot (logos arrive first)
      if (cache.screenshotUrl || attempts >= MAX_POLLS) {
        stopBrandPrefetch();
      }
    } catch {
      // Will retry
    }
    if (attempts >= MAX_POLLS) stopBrandPrefetch();
  };

  poll();
  pollTimer = setInterval(poll, POLL_MS);
}

export function stopBrandPrefetch() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  polling = false;
}

export function getBrandCache(): BrandCache | null {
  return cache;
}

export function clearBrandCache() {
  cache = null;
  stopBrandPrefetch();
}
